import { useMemo } from "react";

import type { MenuItemDto, MenuTypeDto } from "$src/core/models/dto";
import useMenuQuery from "$src/core/queries/use-menu-query";
import Logging from "$src/core/services/logging/logging";

export type TabBarMenuItem = {
  name: string;
  slot: number;
  type?: MenuTypeDto;
  slug: string;
  path?: string;
  label: string;
  icon?: string;
};

export type NavBarMenuItem = {
  name: string;
  path?: string;
  label: string;
  icon?: string;
};

// One (slot-N)/[slug] route group exists per menu item; extra items get no route.
const MAX_TAB_SLOTS = 7;

const getItemSlug = (item: MenuItemDto) =>
  (item.type ?? item.path)?.replace(/^\/+/, "") ?? "";

const processNavItems = (items: MenuItemDto[]): NavBarMenuItem[] =>
  items
    .filter((item) => item.type || item.path)
    .map((item) => ({
      name: getItemSlug(item),
      path: item.path,
      label: item.label,
      icon: item.icon?.uri,
    }));

const processTabBarItems = (items: MenuItemDto[]): TabBarMenuItem[] => {
  let usedSlots = 0;

  return items
    .filter((item) => item.type || item.path)
    .map((item) => {
      const slot = ++usedSlots;

      if (usedSlots > MAX_TAB_SLOTS) {
        Logging.warn(`Too many menu items, max is ${MAX_TAB_SLOTS}`);
        return undefined;
      }

      return {
        name: `(slot-${slot})/[slug]`,
        slot,
        type: item.type,
        slug: getItemSlug(item),
        path: item.path,
        label: item.label,
        icon: item.icon?.uri,
      };
    })
    .filter(Boolean);
};

const useMenu = () => {
  const { data: menu } = useMenuQuery();

  const items = useMemo(() => {
    if (!menu) return undefined;

    // Web's nav bar can hold items outside the tab bar; they still need a slot route.
    const routableItems = menu.tabBar.concat(
      menu.navBar.filter(
        (item) =>
          !menu.tabBar.some(
            (tabItem) => getItemSlug(tabItem) === getItemSlug(item),
          ),
      ),
    );

    return {
      tabBar: processTabBarItems(menu.tabBar),
      navBar: processNavItems(menu.navBar),
      routable: processTabBarItems(routableItems),
    };
  }, [menu]);

  return items;
};

export default useMenu;
